import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { lessonTypeLabel, lessonTimeLabel } from './clientPortalSelectors';
import { displayDate } from '../common/datePresentation';
import { cancellationKind, displaySchoolTimestamp } from './clientSchedulePresentation';
import TeacherIdentity from './TeacherIdentity';

export default function LessonCard({ lesson, lessonTypes, disabled, confirming, onRequest, onDismiss, onConfirm, onUndo, variant, showDate }) {
  const { t: tr, i18n } = useTranslation();
  const actionRef = useRef(null);
  const kind = cancellationKind(lesson);
  const cancellation = Object.prototype.hasOwnProperty.call(lesson, 'cancellationInfo') ? lesson.cancellationInfo || {} : lesson.cancellation || {};
  const reported = displaySchoolTimestamp(cancellation.reportedAt || cancellation.cancelledAt, i18n.language);
  const recorded = displaySchoolTimestamp(cancellation.recordedAt, i18n.language);
  const dismiss = () => { onDismiss(); if (actionRef.current) actionRef.current.focus(); };
  const closeConfirmation = event => { if (event.key === 'Escape' && confirming && !disabled) dismiss(); };
  const canCancel = lesson.canCancel && !kind;
  const canUndo = lesson.canUndoCancellation && kind === 'participant';
  return (
    <article className={`client-portal__lesson-card${variant === 'schedule' ? ' client-portal__lesson-card--schedule' : ''}${kind === 'participant' ? ' client-portal__lesson-card--cancelled' : ''}`}>
      {showDate && <div className="client-portal__lesson-date">{displayDate(lesson.date, i18n.language)}</div>}
      <div className="client-portal__lesson-row">
        <div className="client-portal__lesson-time">{lessonTimeLabel(lesson, lessonTypes, i18n.language)}</div>
        <div className="client-portal__lesson-information">
          <div className="client-portal__lesson-student">{lesson.studentName}</div>
          <div className="client-portal__lesson-type">{lessonTypeLabel(lesson.classTypeId, lessonTypes, i18n.language)}</div>
          <TeacherIdentity teacher={lesson.teacher} />
          {kind && (
          <div className="client-portal__lesson-cancellation">
            <p className="client-portal__status">{tr(kind === 'school' ? 'client:schedule.schoolCancelled' : 'client:schedule.participationCancelled')}</p>
            <div className="client-portal__cancellation-times">
              {kind === 'school'
                ? <p>{recorded ? <>{tr('client:schedule.cancelledAt')} <time dateTime={cancellation.recordedAt}>{recorded}</time></> : tr('client:schedule.cancelTimeUnavailable')}</p>
                : (
                  <>
                    <p>{reported ? <>{tr('client:schedule.reportedAt')} <time dateTime={cancellation.reportedAt || cancellation.cancelledAt}>{reported}</time></> : tr('client:schedule.reportTimeUnavailable')}</p>
                    {recorded && Date.parse(cancellation.recordedAt) !== Date.parse(cancellation.reportedAt || cancellation.cancelledAt) && (
                      <p>{tr('client:schedule.recordedAt')} <time dateTime={cancellation.recordedAt}>{recorded}</time></p>
                    )}
                  </>
                )}
              {canUndo && <p>{tr('client:schedule.undoHint')}</p>}
              {kind === 'school' && <p>{tr('client:schedule.schoolHint')}</p>}
            </div>
          </div>
          )}
        </div>
        <div className="client-portal__lesson-actions">
          {canCancel && <button ref={actionRef} type="button" className="client-portal__absence-link" disabled={disabled} onKeyDown={closeConfirmation} onClick={confirming ? dismiss : onRequest}>{tr('client:reportAnAbsence_55')}</button>}
          {canUndo && <button type="button" className="btn manage-teal-button" disabled={disabled} onClick={onUndo}>{tr('manage:undoCancellation_4')}</button>}
        </div>
      </div>
      {confirming && canCancel && (
      <div className="client-portal__confirmation" role="group" aria-label={tr("client:confirmCancellation_56")}>
        <h3>{tr('client:schedule.confirmHeading')}</h3>
        <p>{lesson.studentName} · {displayDate(lesson.date, i18n.language)}, {lessonTimeLabel(lesson, lessonTypes, i18n.language)}</p>
        <p>{tr('client:schedule.confirmMessage')}</p>
        <p>{tr('client:schedule.undoAfterReport')}</p>
        <div className="client-portal__lesson-actions">
          <button type="button" className="btn manage-teal-button" disabled={disabled} onKeyDown={closeConfirmation} onClick={dismiss}>{tr("common:cancel")}</button>
          <button type="button" className="btn manage-red-button" disabled={disabled} onKeyDown={closeConfirmation} onClick={onConfirm}>{tr("client:confirmCancellation_57")}</button>
        </div>
      </div>
      )}
    </article>
  );
}
LessonCard.propTypes = {
  lesson: PropTypes.object.isRequired,
  lessonTypes: PropTypes.array.isRequired,
  disabled: PropTypes.bool.isRequired,
  confirming: PropTypes.bool.isRequired,
  onRequest: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['dashboard', 'schedule']),
  showDate: PropTypes.bool,
};
LessonCard.defaultProps = { variant: 'dashboard', showDate: true };
